import type { ReactNode } from "react";
import { combineClasses } from "@/lib/utils";
import styles from "./StatCard.module.scss";

export interface StatCardData {
  label: string;
  value: string | number;
  delta?: string;
  // Color for `delta` — "positive" (green) is the default since a delta is
  // usually good news; "negative" (red) for figures like churn/overdue,
  // "neutral" (amber) for a change that's neither clearly good nor bad
  // (e.g. a growing pending-review queue).
  deltaTone?: "positive" | "negative" | "neutral";
  caption?: string;
  icon?: ReactNode;
  // Colors the big number itself — for a figure that's the point of the
  // card (e.g. a count of failed sign-ins), not a secondary `delta`.
  // Omit for the default gray-900.
  valueTone?: "danger" | "warning";
  onClick?: () => void;
}

const StatCard = ({ label, value, delta, deltaTone = "positive", caption, icon, valueTone, onClick }: StatCardData) => (
  <div
    className={combineClasses(styles.statCard, onClick && styles.clickable)}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
  >
    <div className={styles.statHeader}>
      <p className={styles.statLabel}>{label}</p>
      {icon && <span className={styles.statIcon}>{icon}</span>}
    </div>
    <span
      className={combineClasses(
        styles.statValue,
        valueTone === "danger" && styles.statValueDanger,
        valueTone === "warning" && styles.statValueWarning,
      )}
    >
      {value}
    </span>
    {(delta || caption) && (
      <div className={styles.statMetaRow}>
        {delta && (
          <span
            className={combineClasses(
              styles.statDelta,
              deltaTone === "negative" && styles.statDeltaNegative,
              deltaTone === "neutral" && styles.statDeltaNeutral,
            )}
          >
            <span className={styles.statDeltaArrow}>{deltaTone === "negative" ? "↘" : "↗"}</span>
            {delta}
          </span>
        )}
        {caption && <span className={styles.statCaption}>{caption}</span>}
      </div>
    )}
  </div>
);

export interface StatCardsRowProps {
  stats: StatCardData[];
}

const StatCardsRow = ({ stats }: StatCardsRowProps) => (
  <div className={styles.statsRow}>
    {stats.map((stat) => (
      <StatCard key={stat.label} {...stat} />
    ))}
  </div>
);

export default StatCardsRow;
export { StatCard, StatCardsRow };
